import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChatTeardropText } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';

const FirstMessageEditor = ({ onMobileSidebarToggle, isMobile, isSidePanelOpen }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [firstQuestion, setFirstQuestion] = useState('');
  const [initialFirstQuestion, setInitialFirstQuestion] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [settingsId, setSettingsId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError) throw new Error(accountIdError);

        const { data, error } = await supabase
          .from('vapi_call_settings')
          .select('id, company_name, first_question')
          .eq('account_id', accountId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (data) {
          setCompanyName(data.company_name || '');
          const initialQuestion = data.first_question || 'How can I help you today?';
          setFirstQuestion(initialQuestion);
          setInitialFirstQuestion(initialQuestion);
          setSettingsId(data.id);
        } else {
          const defaultQuestion = 'How can I help you today?';
          setFirstQuestion(defaultQuestion);
          setInitialFirstQuestion(defaultQuestion);
          setCompanyName('your company');
        }
      } catch (err) {
        console.error('Error fetching call settings:', err);
        toast.error('Failed to load settings.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [supabase]);

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  const handleSave = async () => {
    if (!firstQuestion.trim()) {
        toast.error("First question cannot be empty.");
        return;
    }
    setIsSaving(true);
    try {
      if (!settingsId) {
        toast.error("Settings could not be found for this account.");
        return;
      }

      const { error } = await supabase
        .from('vapi_call_settings')
        .update({ first_question: firstQuestion.trim() })
        .eq('id', settingsId);

      if (error) throw error;

      toast.success('First message saved successfully');
      setInitialFirstQuestion(firstQuestion.trim());
    } catch (err) {
      console.error('Error saving first question:', err);
      toast.error('Failed to save first message.');
    } finally {
      setIsSaving(false);
    }
  };

  const previewText = `Welcome back to ${companyName || 'your company'}! ${firstQuestion}`;

  const hasChanges = firstQuestion.trim() !== initialFirstQuestion.trim();

  return (
    <motion.div
      key="first-message-editor"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
        <div className="p-4 sm:p-6 flex-grow flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                        <ChatTeardropText size={20} className="text-gray-700" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">First Message</h2>
                        <p className="text-sm text-gray-500">
                            Customize the first thing your AI says to callers.
                        </p>
                    </div>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSave}
                    disabled={isSaving || isLoading || !hasChanges}
                    className="py-2.5 px-6 rounded-lg bg-black text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                    {isSaving ? (
                        <>
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                            />
                            Saving...
                        </>
                    ) : (
                        "Save Changes"
                    )}
                </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-6 overflow-y-auto pr-2">
                {isLoading ? (
                    <div className="flex items-center justify-center h-48">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="h-8 w-8 border-4 border-black border-t-transparent rounded-full"
                        />
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your AI's first question</label>
                            <textarea
                                ref={inputRef}
                                value={firstQuestion}
                                onChange={(e) => setFirstQuestion(e.target.value)}
                                placeholder="e.g., How can I help you today?"
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                rows="4"
                                disabled={isSaving}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Message Preview</label>
                            <div className="p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50">
                                <p className="text-gray-600 text-sm italic">
                                    "{previewText}"
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    </motion.div>
  );
};

export default FirstMessageEditor; 